import React from 'react';
import T from 'i18n';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Popover,
  Typography,
} from '@mui/material';
import {
  Clear as ClearIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { selectCurrentCaseId } from 'common/selectors';
import FilesBundlesBrowsePanel, { BundleSelection } from './FilesBundlesBrowsePanel';
import type { FileItem } from './FilesSelectionPanel';

type SelectionMode = 'none' | 'files' | 'bundles';

export type AllowedMode = 'files' | 'bundles' | 'both';

const ANCHOR_PLACEHOLDER_ID = '__ANCHOR_PLACEHOLDER__';

interface FilesAndBundlesSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (result: { files: FileItem[]; bundles: BundleSelection[] }) => void;
  initialSelectedFiles: FileItem[];
  initialSelectedBundles: BundleSelection[];
  allowedMode?: AllowedMode;
  compareMode?: boolean;
  lockedAnchorFile?: FileItem | null;
  variant?: 'dialog' | 'popover';
  anchorEl?: HTMLElement | null;
}

function deriveSelectionMode(files: FileItem[], bundles: BundleSelection[]): SelectionMode {
  if (files.length > 0) return 'files';
  if (bundles.length > 0) return 'bundles';
  return 'none';
}

export default function FilesAndBundlesSelectionDialog({
  open,
  onClose,
  onSave,
  initialSelectedFiles,
  initialSelectedBundles,
  allowedMode = 'both',
  compareMode = false,
  lockedAnchorFile = null,
  variant = 'dialog',
  anchorEl = null,
}: FilesAndBundlesSelectionDialogProps) {
  const caseId = useSelector(selectCurrentCaseId);

  const [selectedFiles, setSelectedFiles] = React.useState<FileItem[]>(initialSelectedFiles);
  const [selectedBundles, setSelectedBundles] =
    React.useState<BundleSelection[]>(initialSelectedBundles);
  const [selectionMode, setSelectionMode] = React.useState<SelectionMode>(
    deriveSelectionMode(initialSelectedFiles, initialSelectedBundles),
  );
  const effectiveAnchorId = lockedAnchorFile ? lockedAnchorFile.id : null;
  const [anchorFileId, setAnchorFileId] = React.useState<string | null>(
    effectiveAnchorId || (initialSelectedFiles.length > 0 ? initialSelectedFiles[0].id : null),
  );

  // Reset state when dialog opens
  React.useEffect(() => {
    if (open) {
      if (lockedAnchorFile && compareMode) {
        // Ensure locked anchor is always at position 0
        const otherFiles = initialSelectedFiles.filter((f) => f.id !== lockedAnchorFile.id);
        setSelectedFiles([lockedAnchorFile, ...otherFiles]);
        setAnchorFileId(lockedAnchorFile.id);
        setSelectionMode('files');
      } else {
        setSelectedFiles(initialSelectedFiles);
        setAnchorFileId(initialSelectedFiles.length > 0 ? initialSelectedFiles[0].id : null);
        setSelectionMode(deriveSelectionMode(initialSelectedFiles, initialSelectedBundles));
      }
      setSelectedBundles(initialSelectedBundles);
    }
  }, [open, initialSelectedFiles, initialSelectedBundles, lockedAnchorFile, compareMode]);

  const handleFileSelect = (file: FileItem) => {
    if (selectionMode === 'bundles') return;

    if (compareMode) {
      setSelectedFiles((prev) => {
        const isSelected = prev.some((f) => f.id === file.id);
        if (isSelected) {
          // Prevent removing locked anchor
          if (lockedAnchorFile && file.id === lockedAnchorFile.id) return prev;
          // Removing a file
          if (file.id === anchorFileId) {
            // Removing anchor — replace with placeholder
            setAnchorFileId(null);
            const placeholder: FileItem = {
              id: ANCHOR_PLACEHOLDER_ID,
              name: '',
              isPlaceholder: true,
            };
            const remaining = prev.filter((f) => f.id !== file.id);
            return [placeholder, ...remaining];
          }
          return prev.filter((f) => f.id !== file.id);
        } else {
          // Adding a file
          if (selectionMode === 'none') {
            setSelectionMode('files');
          }
          if (prev.length === 0) {
            // First file becomes anchor
            setAnchorFileId(file.id);
            return [file];
          }
          if (prev[0]?.isPlaceholder) {
            // Fill the empty anchor slot
            setAnchorFileId(file.id);
            return [file, ...prev.slice(1)];
          }
          // Append as comparison file
          return [...prev, file];
        }
      });
    } else {
      setSelectedFiles((prev) => {
        const isSelected = prev.some((f) => f.id === file.id);
        if (isSelected) {
          const next = prev.filter((f) => f.id !== file.id);
          if (next.length === 0) {
            setSelectionMode('none');
          }
          return next;
        } else {
          if (selectionMode === 'none') {
            setSelectionMode('files');
          }
          return [...prev, file];
        }
      });
    }
  };

  const handleToggleAllFolderFiles = React.useCallback(
    (files: FileItem[], folderId: string, selectAll: boolean) => {
      if (selectionMode === 'bundles' || compareMode) return;

      if (selectAll) {
        if (selectionMode === 'none') {
          setSelectionMode('files');
        }
        setSelectedFiles((prev) => {
          const existingIds = new Set(prev.map((f) => f.id));
          const newFiles = files
            .filter((f) => !existingIds.has(f.id))
            .map((f) => ({ ...f, folderId }));
          return [...prev, ...newFiles];
        });
      } else {
        const folderFileIds = new Set(files.map((f) => f.id));
        setSelectedFiles((prev) => {
          const next = prev.filter((f) => !folderFileIds.has(f.id));
          if (next.length === 0) {
            setSelectionMode('none');
          }
          return next;
        });
      }
    },
    [selectionMode, compareMode],
  );

  const handleBundleSelect = (bundle: BundleSelection) => {
    if (selectionMode === 'files') return;

    setSelectionMode('bundles');
    setSelectedBundles((prev) => {
      if (prev.some((b) => String(b.id) === String(bundle.id))) return prev;
      return [...prev, bundle];
    });
  };

  const handleBundleDeselect = (bundle: BundleSelection) => {
    setSelectedBundles((prev) => {
      const next = prev.filter((b) => String(b.id) !== String(bundle.id));
      if (next.length === 0) {
        setSelectionMode('none');
      }
      return next;
    });
  };

  const hadInitialSelections = initialSelectedFiles.length > 0 || initialSelectedBundles.length > 0;

  const handleClearAll = () => {
    if (lockedAnchorFile && compareMode) {
      setSelectedFiles([lockedAnchorFile]);
      setAnchorFileId(lockedAnchorFile.id);
      setSelectionMode('files');
    } else {
      setSelectedFiles([]);
      setAnchorFileId(null);
      setSelectionMode('none');
    }
    setSelectedBundles([]);
  };

  const handleClearTab = React.useCallback(
    (leafIds: Set<string>) => {
      setSelectedFiles((prev) => {
        const next = prev.filter((f) => !f.folderId || !leafIds.has(f.folderId));
        if (next.length === 0) {
          setSelectionMode('none');
          setAnchorFileId(null);
        }
        return next;
      });
      setSelectedBundles((prev) => {
        const next = prev.filter((b) => !leafIds.has(String(b.id)));
        if (
          next.length === 0 &&
          selectedFiles.every((f) => f.folderId && leafIds.has(f.folderId))
        ) {
          setSelectionMode('none');
        }
        return next;
      });
    },
    [selectedFiles],
  );

  const nonPlaceholderFiles = selectedFiles.filter((f) => !f.isPlaceholder);

  const handleSave = () => {
    onSave({ files: nonPlaceholderFiles, bundles: selectedBundles });
  };

  const totalCount = compareMode
    ? nonPlaceholderFiles.length + selectedBundles.length
    : selectedFiles.length + selectedBundles.length;

  const isSaveDisabled = compareMode
    ? nonPlaceholderFiles.length < 2
    : totalCount === 0 && !hadInitialSelections;

  // Remove a file from the right pane (non-compare mode or comparison files)
  const handleRemoveFile = (fileId: string) => {
    // Prevent removing locked anchor
    if (lockedAnchorFile && fileId === lockedAnchorFile.id) return;
    if (compareMode && fileId === anchorFileId) {
      // Removing anchor — replace with placeholder
      setAnchorFileId(null);
      setSelectedFiles((prev) => {
        const placeholder: FileItem = {
          id: ANCHOR_PLACEHOLDER_ID,
          name: '',
          isPlaceholder: true,
        };
        const remaining = prev.filter((f) => f.id !== fileId);
        return [placeholder, ...remaining];
      });
    } else {
      setSelectedFiles((prev) => {
        const next = prev.filter((f) => f.id !== fileId);
        if (next.filter((f) => !f.isPlaceholder).length === 0) {
          setSelectionMode('none');
          setAnchorFileId(null);
          return [];
        }
        return next;
      });
    }
  };

  // Render a file item row in the right pane
  const renderFileItem = (file: FileItem) => (
    <ListItem
      key={`file-${file.id}`}
      sx={{
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        '&:last-child': { borderBottom: 'none' },
        px: 1,
        py: 1,
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FileIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1, flexShrink: 0 }} />
            <Typography
              variant="body2"
              sx={{
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                lineHeight: 1.4,
                fontWeight: 'medium',
              }}
            >
              {file.name}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 3.5 }}>
            ID: {file.id}
            {file.pageCount && ` \u2022 ${file.pageCount} pages`}
          </Typography>
        </Box>
        {!(lockedAnchorFile && file.id === lockedAnchorFile.id) ? (
          <IconButton
            size="small"
            onClick={() => handleRemoveFile(file.id)}
            sx={{ cursor: 'pointer', flexShrink: 0 }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Box>
    </ListItem>
  );

  // Right pane content for compare mode
  const renderCompareRightPane = () => {
    const anchor = selectedFiles[0];
    const comparisonFiles = selectedFiles.slice(1).filter((f) => !f.isPlaceholder);
    const hasAnchor = anchor && !anchor.isPlaceholder;

    return (
      <List sx={{ p: 0 }}>
        {/* Anchor File Section */}
        <Typography
          variant="caption"
          sx={{ px: 1, py: 0.5, display: 'block', fontWeight: 600, color: '#4567E5' }}
        >
          {T.translate('generic.anchorFile')}
        </Typography>
        {hasAnchor ? (
          renderFileItem(anchor)
        ) : (
          <ListItem sx={{ px: 1, py: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {T.translate('generic.anchorFileRemovedInfo')}
            </Typography>
          </ListItem>
        )}

        {/* Comparison Files Section */}
        <Typography
          variant="caption"
          sx={{ px: 1, py: 0.5, mt: 1, display: 'block', fontWeight: 600, color: '#4567E5' }}
        >
          {T.translate('generic.comparisonFiles')} ({comparisonFiles.length})
        </Typography>
        {comparisonFiles.length > 0 ? (
          comparisonFiles.map(renderFileItem)
        ) : (
          <ListItem sx={{ px: 1, py: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {T.translate('generic.searchOrBrowseToFindFiles')}
            </Typography>
          </ListItem>
        )}
      </List>
    );
  };

  const isPopover = variant === 'popover';

  const content = (
    <>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'row',
          gap: isPopover ? 1.5 : 2,
          overflow: 'hidden',
        }}
      >
        {/* Left Column — Browse Panel */}
        <Box sx={{ width: '60%', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <FilesBundlesBrowsePanel
            onFileSelect={handleFileSelect}
            selectedFiles={selectedFiles}
            onBundleSelect={handleBundleSelect}
            onBundleDeselect={handleBundleDeselect}
            selectedBundles={selectedBundles}
            selectionMode={selectionMode}
            caseId={caseId}
            allowedMode={allowedMode}
            onClearTab={handleClearTab}
            onToggleAllFolderFiles={handleToggleAllFolderFiles}
            compareMode={compareMode}
          />
        </Box>

        {/* Right Column — Selected Items */}
        <Box
          sx={{
            width: '40%',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            borderLeft: '1px solid rgba(0,0,0,0.12)',
            pl: isPopover ? 1.5 : 2,
          }}
        >
          <Box
            sx={{
              mb: isPopover ? 1 : 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant={isPopover ? 'body2' : 'subtitle1'} sx={{ fontWeight: 'medium' }}>
              {T.translate('generic.selected')} ({totalCount})
            </Typography>
            {totalCount > 0 && (
              <Button
                size="small"
                onClick={handleClearAll}
                sx={{ textTransform: 'none', fontSize: '12px' }}
              >
                {T.translate('generic.clearAll')}
              </Button>
            )}
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            {compareMode ? (
              selectedFiles.length === 0 ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{ height: '100%', textAlign: 'center' }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {T.translate('generic.noFilesSelected')}
                    <br />
                    {T.translate('generic.searchOrBrowseToFindFiles')}
                  </Typography>
                </Box>
              ) : (
                renderCompareRightPane()
              )
            ) : totalCount === 0 ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{ height: '100%', textAlign: 'center' }}
              >
                <Typography variant="body2" color="text.secondary">
                  {T.translate('generic.noFilesSelected')}
                  <br />
                  {T.translate('generic.searchOrBrowseToFindFiles')}
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {/* Selected Bundles */}
                {selectedBundles.map((bundle) => (
                  <ListItem
                    key={`bundle-${bundle.id}`}
                    sx={{
                      borderBottom: '1px solid rgba(0,0,0,0.1)',
                      '&:last-child': { borderBottom: 'none' },
                      px: 1,
                      py: isPopover ? 0.5 : 1,
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                        <FolderIcon
                          fontSize="small"
                          sx={{ color: '#0E172A', mr: 1, flexShrink: 0 }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                            lineHeight: 1.4,
                            fontWeight: 'medium',
                          }}
                        >
                          {bundle.name}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleBundleDeselect(bundle)}
                        sx={{ cursor: 'pointer', flexShrink: 0 }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItem>
                ))}

                {/* Selected Files */}
                {selectedFiles.map(renderFileItem)}
              </List>
            )}
          </Box>
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          mt: 'auto',
          pt: isPopover ? 1.5 : 2,
          mb: isPopover ? 0 : '20px',
          pr: isPopover ? 0 : '20px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          borderTop: '1px solid rgba(0,0,0,0.12)',
          backgroundColor: 'background.paper',
          ...(!isPopover && { position: 'sticky' as const, bottom: '20px' }),
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          color="inherit"
          size={isPopover ? 'small' : 'medium'}
          sx={{
            color: '#fff',
            bgcolor: 'text.disabled',
            ':hover': { bgcolor: 'text.secondary' },
          }}
        >
          {T.translate('generic.close')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaveDisabled}
          size={isPopover ? 'small' : 'medium'}
        >
          {T.translate('generic.select')} ({totalCount})
        </Button>
      </Box>
    </>
  );

  if (isPopover) {
    return (
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        marginThreshold={48}
        PaperProps={{
          sx: {
            width: 1100,
            height: 675,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            p: 2,
            border: '1px solid #e3e3e3',
          },
        }}
      >
        <Typography sx={{ fontWeight: 600, mb: 1, fontSize: '1.2rem' }}>
          {allowedMode === 'both'
            ? T.translate('generic.selectFilesOrBundles')
            : allowedMode === 'bundles'
              ? T.translate('generic.selectBundles')
              : T.translate('generic.selectFiles')}
        </Typography>
        {content}
      </Popover>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          minHeight: '75vh',
          width: '75vw',
          maxWidth: '75vw',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle sx={{ fontSize: '1.2rem' }}>
        {allowedMode === 'both'
          ? T.translate('generic.selectFilesOrBundles')
          : allowedMode === 'bundles'
            ? T.translate('generic.selectBundles')
            : T.translate('generic.selectFiles')}
      </DialogTitle>
      <DialogContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          px: 3,
          pt: 0,
          pb: 0,
          overflow: 'hidden',
        }}
      >
        {content}
      </DialogContent>
    </Dialog>
  );
}
